/**
 * kumo.saves / stats / leaderboards / achievements / inventory — M1 ゲームデータ。
 * サーバー側は apps/server/src/modules/gamedata（README.md に API 一覧）。
 *
 *   await kumo.saves.set('slot1', state);                 // クラウドセーブ（ifVersion で競合検出）
 *   kumo.stats.add('kills', 1);                            // 250ms まとめて送信（await 不要）
 *   kumo.stats.submit('best_time', 61234);
 *   await kumo.leaderboards.get('fastest', { limit: 10 });
 *   kumo.achievements.on('unlocked', (a) => toast(a.title));
 *   await kumo.inventory.consume({ currencies: { gems: 50 } });
 *   await kumo.inventory.exchange('buy_red_skin');         // config の exchanges（価格・中身はサーバー側）
 *
 * 所持品・通貨を「増やす」関数は無い（サーバーの実績報酬・課金・管理 API・交換所だけが増やせる。改造クライアント対策）。
 * exchange は「マスターデータに書かれた交換を実行する」だけで、何をいくつ払い何を得るかはクライアントから変えられない。
 */

import { Emitter, KumoError, type KumoCore } from './core.js';

// ---- 型（サーバーの応答形式と一致させる） -----------------------------------------

export type Text = string | Record<string, string>;
export type Aggregation = 'sum' | 'max' | 'min' | 'latest';
export type Reset = 'none' | 'daily' | 'weekly' | 'monthly';
export interface GrantLine { currency?: string; item?: string; count: number }

export interface SaveMeta { key: string; version: number; size: number; updatedAt: string }
export interface Save<T = unknown> extends SaveMeta { data: T }

export interface UnlockedAchievement { key: string; title: Text | null; period: string; granted: GrantLine[] }
export interface StatsResult {
  /** stat → 期間キー → 値（'all' は通算） */
  stats: Record<string, Record<string, number>>;
  rejected: Array<{ key: string; reason: string; message: string }>;
  unlocked: UnlockedAchievement[];
}
/** 実績を隠している間（launchHidden.ts）に kumo.stats の型が見せる結果（達成した実績 unlocked を外したもの） */
export type PublicStatsResult = Omit<StatsResult, 'unlocked'>;

export interface LeaderboardEntry { rank: number; playerId: string; displayName: string | null; value: number }
export interface Leaderboard {
  leaderboard: { key: string; title: Text | null; stat: string; order: 'asc' | 'desc'; reset: Reset; format: 'number' | 'time_ms' | 'time_s' };
  period: string;
  currentPeriod: string;
  previousPeriod: string | null;
  total: number;
  entries: LeaderboardEntry[];
  me: LeaderboardEntry | null;
}

export interface Achievement {
  key: string;
  /** hidden で未達成なら null */
  title: Text | null;
  description: Text | null;
  hidden: boolean;
  reset: Reset;
  period: string;
  unlocked: boolean;
  unlockedAt: string | null;
  /** true = ゲームが achievements.unlock(key) で達成させる型 */
  manual: boolean;
  progress: { stat: string; value: number | null; target: number; comparison: 'gte' | 'lte' } | null;
  rewards: GrantLine[];
}

export interface Inventory { currencies: Record<string, number>; items: Record<string, number> }

export interface ExchangeDefinition {
  key: string;
  title: Text | null;
  description: Text | null;
  cost: GrantLine[];
  grants: GrantLine[];
  limit: { count: number; reset: Reset } | null;
}

export interface Exchange extends ExchangeDefinition {
  /** 今の期間キー（limit が無ければ 'all'） */
  period: string;
  /** この期間の残り回数（制限なしは null） */
  remaining: number | null;
  /** 受け取る durable / collectible を既に持っている（交換すると already_owned） */
  owned: boolean;
}

export interface ExchangeResult {
  /** false = 同じ Idempotency-Key の再送（何も払っていない。granted は 1 回目に届いた量） */
  applied: boolean;
  key: string;
  spent: GrantLine[];
  granted: GrantLine[];
  remaining: number | null;
  inventory: Inventory;
}

export interface Definitions {
  version: number;
  resetTimeZone: string;
  stats: Array<{ key: string; title: Text | null; aggregation: Aggregation; maxPerSubmit: number | null }>;
  leaderboards: Array<{ key: string; title: Text | null; stat: string; order: 'asc' | 'desc'; reset: Reset; format: string }>;
  currencies: Array<{ key: string; title: Text | null; max: number }>;
  items: Array<{ key: string; title: Text | null; description: Text | null; kind: string; max: number | null }>;
  exchanges: ExchangeDefinition[];
}

export interface GameDataEvents extends Record<string, unknown> {
  unlocked: UnlockedAchievement;
  stats: StatsResult;
}

/**
 * セーブの大きさの上限は SDK に持たない（2026-10-04 creator/limits-clients・洗い出し L104）。
 * 経緯: 2026-09-29 creator/core-dx2 C2 では、サーバーの 256 KB をここに写し（MAX_SAVE_BYTES）、送る前に数えて止めていた。
 *   founder 決定（2026-10-04「制限はとっぱらう」）で Core がセーブの上限を上げる（2MB の既定・プロジェクトごと）= 写しがあると、
 *   サーバーが通す大きさを SDK だけが断る。上限はサーバーの 413 の details.limit から知る（saves.set の中）。
 * 残した WHY（core-dx2 の狙い）: サーバーは「セーブの上限超え」を save_too_large、「本文の上限超え」を invalid_request（どちらも 413・
 *   details.limit 付き・gamedata/routes.ts）で返す。ゲームが見るのは常に save_too_large の 1 つにそろえる（saves.set で言い直す）
 */

/** サーバーの errorGuide.ts の save_too_large の hint と同じ文（SDK が先に止めても、サーバーが返したときと同じ一手を読める） */
const SAVE_TOO_LARGE_HINT = 'Keep each slot under details.limit bytes of JSON: split the data across several slots, or keep large files outside saves.';

/** サーバーと同じ数え方（putSave の JSON.stringify(data) の UTF-8 バイト数）。data が undefined（JSON にならない）は数えない = サーバーの 400 に任せる */
function saveBytes(data: unknown): number | null {
  const json = JSON.stringify(data) as string | undefined;
  return json === undefined ? null : new TextEncoder().encode(json).length;
}

/** stats の送信をまとめる時間。1 フレームごとの add() を 1 リクエストにするため（250ms = 体感の遅れが出ない範囲） */
export const STATS_BATCH_MS = 250;

function newIdempotencyKey(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (c?.randomUUID) return c.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

export function createGameData(core: KumoCore) {
  const events = new Emitter<GameDataEvents>();

  // ---- 定義（stats のまとめ方に使う。1 回だけ取得、失敗したら次回再取得） ----
  let defsPromise: Promise<Definitions> | null = null;
  const definitions = () =>
    (defsPromise ||= core.request<Definitions>('GET', '/v1/gamedata/definitions', undefined, { auth: false }).catch((e) => {
      defsPromise = null;
      throw e;
    }));

  // ---- saves ----
  // 最後に見た版（get / set / list で更新）。set(..., { ifVersion: 'latest' }) で使う
  const knownVersions = new Map<string, number>();
  // サーバーの 413 の details.limit で教わったセーブの上限（未取得 = undefined = 送ってサーバーに判定させる）。
  // ページを開いている間だけ覚える: クリエイターが上限を上げても、読み直せば新しい値で判定される
  let learnedSaveLimit: number | undefined;
  const saves = {
    async list(): Promise<SaveMeta[]> {
      const r = await core.request<{ saves: SaveMeta[] }>('GET', '/v1/saves');
      for (const s of r.saves) knownVersions.set(s.key, s.version);
      return r.saves;
    },
    /** 無ければ null。 */
    async get<T = unknown>(key: string): Promise<Save<T> | null> {
      try {
        const s = await core.request<Save<T>>('GET', `/v1/saves/${encodeURIComponent(key)}`);
        knownVersions.set(key, s.version);
        return s;
      } catch (e) {
        if (e instanceof KumoError && e.status === 404) {
          knownVersions.set(key, 0);
          return null;
        }
        throw e;
      }
    },
    /**
     * 保存。ifVersion:
     *   省略     … 上書き（単純なゲーム向け）
     *   数値     … その版のときだけ書く（0 = まだ無いときだけ）。違えば KumoError code 'version_conflict'
     *   'latest' … この SDK が最後に見た版（未取得なら 0）。別端末で書かれていたら 'version_conflict'
     */
    async set(key: string, data: unknown, opts: { ifVersion?: number | 'latest' } = {}): Promise<SaveMeta> {
      const ifVersion = opts.ifVersion === 'latest' ? knownVersions.get(key) ?? 0 : opts.ifVersion;
      // 送る前に大きさを確かめるのは、サーバーから上限を一度教わった後だけ（無駄な大きい通信を 2 回目から省く）。
      // 文・details はサーバーの save_too_large と同じ形。docsUrl は付けない: docs の場所はサーバーの DOCS_BASE_URL だけが知る
      const size = saveBytes(data);
      const tooLarge = (limit: number | undefined) =>
        new KumoError(413, 'save_too_large', limit === undefined ? `Save data is ${size} bytes; it is larger than the server accepts` : `Save data is ${size} bytes; the limit is ${limit}`, limit === undefined ? { size } : { size, limit }, { hint: SAVE_TOO_LARGE_HINT });
      if (size !== null && learnedSaveLimit !== undefined && size > learnedSaveLimit) throw tooLarge(learnedSaveLimit);
      let r: SaveMeta;
      try {
        r = await core.request<SaveMeta>('PUT', `/v1/saves/${encodeURIComponent(key)}`, ifVersion === undefined ? { data } : { data, ifVersion });
      } catch (e) {
        if (!(e instanceof KumoError) || e.status !== 413) throw e;
        // サーバーが教えた上限を覚える（プロジェクトごとに違ってよい = SDK は数字を持たない）。上限が上がったら次の 413 で覚え直す
        const limit = (e.details as { limit?: unknown } | undefined)?.limit;
        if (typeof limit === 'number' && limit > 0) learnedSaveLimit = limit;
        if (e.code === 'save_too_large') throw e;
        throw tooLarge(typeof limit === 'number' ? limit : undefined);
      }
      knownVersions.set(key, r.version);
      return r;
    },
    async delete(key: string, opts: { ifVersion?: number } = {}): Promise<void> {
      const q = opts.ifVersion === undefined ? '' : `?ifVersion=${opts.ifVersion}`;
      await core.request('DELETE', `/v1/saves/${encodeURIComponent(key)}${q}`);
      knownVersions.delete(key);
    }
  };

  // ---- stats（まとめ送り） ----
  // WHY まとめる: ゲームは「敵を倒すたびに add('kills', 1)」と書くのが自然。そのまま送ると 1 秒に数十リクエストになる。
  // サーバーと同じ集計方法で手元で畳み込めば（sum は足す・max は大きい方…）、結果は 1 件ずつ送った場合と一致する。
  interface Batch { values: Map<string, number>; waiters: Array<{ resolve: (r: StatsResult) => void; reject: (e: unknown) => void }> }
  let batch: Batch | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  // 定義の取得待ちでまだ batch に入っていない add/submit。
  // WHY（2026-09-26 テンプレート作成中に発見）: add() を await せず直後に leaderboards.get() を呼ぶと、
  // get() の flush は「まだ batch に入っていない（定義待ちの）値」を知らずに空振りし、今の値がランキングに出なかった。
  // 読み出し系は settle() でこれらが batch に入るのを待ってから送る。
  const accepting = new Set<Promise<unknown>>();

  const flush = (opts: { keepalive?: boolean } = {}): Promise<StatsResult | null> => {
    if (timer) clearTimeout(timer);
    timer = null;
    const current = batch;
    batch = null;
    if (!current || current.values.size === 0) return Promise.resolve(null);
    const body = { stats: Object.fromEntries(current.values) };
    const sent = core.request<StatsResult>('POST', '/v1/stats', body, { keepalive: opts.keepalive }).then(
      (r) => {
        if (r.rejected.length) console.warn('[game-backend] stats rejected', r.rejected);
        events.emit('stats', r);
        for (const u of r.unlocked) events.emit('unlocked', u);
        for (const w of current.waiters) w.resolve(r);
        return r;
      },
      (e) => {
        for (const w of current.waiters) w.reject(e);
        throw e;
      }
    );
    sent.catch(() => undefined);
    return sent;
  };

  // タブを閉じる・裏に回すときに残りを送る（keepalive: ページが消えても送信を続けさせる）
  const g = globalThis as unknown as { addEventListener?: (t: string, fn: () => void) => void; document?: { visibilityState?: string } };
  if (typeof g.addEventListener === 'function') {
    g.addEventListener('pagehide', () => void flush({ keepalive: true }).catch(() => undefined));
    g.addEventListener('visibilitychange', () => {
      if (g.document?.visibilityState === 'hidden') void flush({ keepalive: true }).catch(() => undefined);
    });
  }

  const enqueue = (key: string, value: number, mode: 'add' | 'submit'): Promise<StatsResult> => {
    const p = new Promise<StatsResult>((resolve, reject) => {
      if (typeof value !== 'number' || !Number.isFinite(value)) {
        reject(new KumoError(0, 'invalid_value', `stats value for ${key} must be a finite number`));
        return;
      }
      // 定義が取れない（オフライン等）ときは集計方法が分からない → add は sum、submit は同じキーが来たら先に送る
      const step = definitions()
        .then((d) => d.stats.find((s) => s.key === key)?.aggregation, () => undefined)
        .then((agg) => {
          if (mode === 'add' && agg && agg !== 'sum') {
            throw new KumoError(0, 'wrong_aggregation', `stats.add() is for sum stats; ${key} is "${agg}", use stats.submit()`);
          }
          const effective: Aggregation | undefined = mode === 'add' ? 'sum' : agg;
          if (!effective && batch?.values.has(key)) void flush();
          if (!batch) batch = { values: new Map<string, number>(), waiters: [] };
          const b: Batch = batch;
          const prev = b.values.get(key);
          let next = value;
          if (prev !== undefined) {
            if (effective === 'sum') next = prev + value;
            else if (effective === 'max') next = Math.max(prev, value);
            else if (effective === 'min') next = Math.min(prev, value);
          }
          b.values.set(key, next);
          b.waiters.push({ resolve, reject });
          if (!timer) timer = setTimeout(() => void flush().catch(() => undefined), STATS_BATCH_MS);
        })
        .catch(reject);
      accepting.add(step);
      void step.finally(() => accepting.delete(step));
    });
    // 投げっぱなし（await しない）で使われても unhandled rejection にしない。await した呼び出し側には届く
    p.catch(() => undefined);
    return p;
  };

  /** 受付途中の add/submit が batch に入るのを待ってから送る（読み出し系・明示 flush 用） */
  const settle = async (): Promise<StatsResult | null> => {
    while (accepting.size) await Promise.allSettled([...accepting]);
    return flush();
  };

  const stats = {
    /** sum の stat に増分を足す（例: add('kills', 1)）。 */
    add: (key: string, amount = 1) => enqueue(key, amount, 'add'),
    /** max / min / latest の stat に値を送る（例: submit('best_time', 61234)）。 */
    submit: (key: string, value: number) => enqueue(key, value, 'submit'),
    /** まとめ待ちをすぐ送る（ステージクリア直後にランキングを出す前など）。 */
    flush: () => settle(),
    /** 自分の通算値。 */
    async get(): Promise<Record<string, number>> {
      await settle().catch(() => undefined);
      return (await core.request<{ stats: Record<string, number> }>('GET', '/v1/stats')).stats;
    },
    on: <K extends keyof GameDataEvents>(event: K, fn: (v: GameDataEvents[K]) => void) => events.on(event, fn)
  };

  // ---- leaderboards ----
  const leaderboards = {
    /**
     * period: 省略 = 今のシーズン / 'previous' = 前シーズン / 期間キー（'d:2026-09-26' 等）
     * around: 'me' なら自分の前後
     * まとめ待ちの stats を先に送ってから読む（直前の submit が反映されていないランキングを見せない）。
     */
    async get(key: string, opts: { limit?: number; period?: string; around?: 'me' } = {}): Promise<Leaderboard> {
      await settle().catch(() => undefined);
      const params = new URLSearchParams();
      if (opts.limit !== undefined) params.set('limit', String(opts.limit));
      if (opts.period) params.set('period', opts.period);
      if (opts.around) params.set('around', opts.around);
      const qs = params.toString();
      return core.request<Leaderboard>('GET', `/v1/leaderboards/${encodeURIComponent(key)}${qs ? `?${qs}` : ''}`);
    }
  };

  // ---- achievements ----
  const achievements = {
    async list(): Promise<Achievement[]> {
      await settle().catch(() => undefined);
      const r = await core.request<{ achievements: Achievement[]; newlyUnlocked: UnlockedAchievement[] }>('GET', '/v1/achievements');
      for (const u of r.newlyUnlocked) events.emit('unlocked', u);
      return r.achievements;
    },
    /** condition の無い実績を達成させる。すでに達成済みなら newlyUnlocked: false（報酬は二重に付かない）。 */
    async unlock(key: string): Promise<{ newlyUnlocked: boolean; period: string; granted: GrantLine[] }> {
      const r = await core.request<UnlockedAchievement & { newlyUnlocked: boolean }>('POST', `/v1/achievements/${encodeURIComponent(key)}/unlock`);
      if (r.newlyUnlocked) events.emit('unlocked', { key: r.key, title: r.title, period: r.period, granted: r.granted });
      return { newlyUnlocked: r.newlyUnlocked, period: r.period, granted: r.granted };
    },
    on: (event: 'unlocked', fn: (a: UnlockedAchievement) => void) => events.on(event, fn)
  };

  // ---- inventory ----
  const inventory = {
    get: () => core.request<Inventory>('GET', '/v1/inventory'),
    /**
     * 全部か無しか。足りなければ KumoError code 'insufficient'（details.shortages）で何も減らない。
     * Idempotency-Key を自動で付ける（通信の再送で二重に減らない）。
     */
    async consume(what: { currencies?: Record<string, number>; items?: Record<string, number> }, opts: { idempotencyKey?: string } = {}): Promise<Inventory> {
      const r = await core.request<{ applied: boolean; inventory: Inventory }>('POST', '/v1/inventory/consume', what, {
        idempotencyKey: opts.idempotencyKey ?? newIdempotencyKey()
      });
      return r.inventory;
    },
    /** 交換所の一覧（config の exchanges + 自分の残り回数・所持済みか） */
    async exchanges(): Promise<Exchange[]> {
      return (await core.request<{ exchanges: Exchange[] }>('GET', '/v1/inventory/exchanges')).exchanges;
    },
    /**
     * 交換（cost を払って grants を受け取る）をサーバーで 1 TX に実行する。失敗しても何も減らない:
     *   insufficient（details.shortages）/ already_owned（details.items）/ limit_reached / exchange_not_found
     * Idempotency-Key を自動で付ける（通信の再送で二重に払わない）。ボタンの連打対策に、1 回の「購入」操作には
     * 同じ opts.idempotencyKey を使い回すとよい（2 回目は applied:false で同じ結果が返る）
     */
    async exchange(key: string, opts: { idempotencyKey?: string } = {}): Promise<ExchangeResult> {
      return core.request<ExchangeResult>('POST', '/v1/inventory/exchange', { key }, { idempotencyKey: opts.idempotencyKey ?? newIdempotencyKey() });
    }
  };

  return { saves, stats, leaderboards, achievements, inventory };
}

export type KumoGameData = ReturnType<typeof createGameData>;
